// v0.7 alerts while the game is open in a tab: the tab title and icon change, a sound plays,
// and (if switched on in Options) the computer shows a desktop pop-up. Nothing runs when
// the browser is closed; that would need real push notifications (a later version).
import { useEffect, useRef } from 'react';
import { play } from './sound.ts';
import { getSettings } from './settings.ts';

const BASE_TITLE = { value: 'Made Men' };
const ICON_PLAIN = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23111111'/%3E%3Cpath d='M5 19h22l-3-3H8z' fill='%23ffcc33'/%3E%3Cpath d='M9 16q7-12 14 0z' fill='%23ffcc33'/%3E%3Crect x='4' y='22' width='24' height='3' fill='%23b3261e'/%3E%3C/svg%3E";
const ICON_DOT = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23111111'/%3E%3Cpath d='M5 19h22l-3-3H8z' fill='%23ffcc33'/%3E%3Cpath d='M9 16q7-12 14 0z' fill='%23ffcc33'/%3E%3Crect x='4' y='22' width='24' height='3' fill='%23b3261e'/%3E%3Ccircle cx='25' cy='7' r='7' fill='%23b3261e' stroke='%23111111' stroke-width='2'/%3E%3C/svg%3E";

function setIcon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

/** Ask the browser for permission to show desktop pop-ups (needs a click). */
export async function enableDesktopAlerts(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}
export function desktopAlertsSupported(): boolean {
  return 'Notification' in window;
}

function popUp(title: string, body: string, tag: string) {
  const s = getSettings();
  if (!s.desktopAlerts || !('Notification' in window) || Notification.permission !== 'granted' || !document.hidden) return;
  try {
    const n = new Notification(title, { body, tag, icon: ICON_DOT });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* some phones only allow pop-ups from a service worker */ }
}

interface Flags { energy: boolean; stamina: boolean; health: boolean; siege: boolean; poker: boolean; race: boolean; auction: boolean; unread: number }

/** Watches the character sheet and raises alerts for things worth coming back for. */
export function useAlerts(me: any, gameName: string) {
  const prev = useRef<any>(null);
  const raceWarned = useRef<number | null>(null);
  const flags = useRef<Flags>({ energy: false, stamina: false, health: false, siege: false, poker: false, race: false, auction: false, unread: 0 });
  const hiddenSince = useRef<number | null>(null);

  const render = () => {
    const f = flags.current;
    const parts: string[] = [];
    if (document.hidden) {
      if (f.siege) parts.push('⚔ Siege!');
      if (f.poker) parts.push('♣ Your turn');
      if (f.energy) parts.push('⚡ Full');
      if (f.race) parts.push('Race soon');
      if (f.auction) parts.push('Auction!');
    }
    if (f.unread > 0) parts.push(`(${f.unread})`);
    document.title = parts.length ? `${parts.join(' ')} ${BASE_TITLE.value}` : BASE_TITLE.value;
    const any = document.hidden && (f.siege || f.poker || f.energy || f.race || f.auction || f.unread > 0);
    setIcon(any ? ICON_DOT : ICON_PLAIN);
  };

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) hiddenSince.current = Date.now();
      else {
        hiddenSince.current = null;
        // Coming back clears the "full" reminders; the tab shows normally again.
        flags.current.energy = false;
        flags.current.stamina = false;
        flags.current.health = false;
        flags.current.race = false;
      }
      render();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!me) return;
    BASE_TITLE.value = me.name ? `${gameName} – ${me.name}` : gameName;
    const p = prev.current;
    prev.current = me;
    const f = flags.current;
    const s = getSettings();
    f.unread = me.unreadUpdates ?? 0;
    if (p && p.id === me.id) {
      const became = (now: boolean, before: boolean) => now && !before;
      if (became(me.energy >= me.maxEnergy, p.energy >= p.maxEnergy)) {
        f.energy = true;
        if (document.hidden && s.alerts) play('bell');
        popUp('Your energy is full', 'Time for some jobs.', 'energy');
      }
      if (became(me.stamina >= me.maxStamina, p.stamina >= p.maxStamina)) {
        f.stamina = true;
        popUp('Your stamina is full', 'Go settle some scores.', 'stamina');
      }
      if (became(me.health >= me.maxHealth, p.health >= p.maxHealth)) f.health = true;
      const sieges = (me.sieges ?? []).filter((x: any) => x.role !== 'attacker');
      const oldIds = new Set((p.sieges ?? []).map((x: any) => x.id));
      const fresh = sieges.filter((x: any) => !oldIds.has(x.id));
      if (fresh.length) {
        if (s.alerts) play('siren');
        const x = fresh[0];
        popUp(x.role === 'defender' ? 'Your turf is under siege!' : 'A friend needs backup!', `${x.attackerName} attacks. The battle is in ${Math.max(1, Math.round((x.endsAt - Date.now()) / 60_000))} minutes.`, `siege-${x.id}`);
      }
      f.siege = sieges.length > 0;
      if (me.pokerTurn && !p.pokerTurn) {
        if (document.hidden && s.alerts) play('knock');
        popUp('Your turn at the poker table', 'You have 30 seconds to act.', 'poker');
      }
      f.poker = !!me.pokerTurn;
      // v0.8: the Friday auction opens; someone outbids you; a trade offer arrives.
      const A = me.houseAuction, PA = p.houseAuction;
      if (A?.status === 'open' && PA?.status === 'upcoming') {
        if (s.alerts) play('gavel');
        popUp('The Friday auction is open', 'One of a kind, two hours only. Place your bid.', 'auction');
      }
      if (PA?.mine && A && !A.mine && A.status === 'open') {
        if (s.alerts) play('alert');
        popUp('You’ve been outbid', 'Somebody topped your bid at the Friday auction.', 'outbid');
      }
      if ((me.pendingTrades ?? 0) > (p.pendingTrades ?? 0)) {
        if (s.alerts) play('envelope');
        popUp('A trade offer for you', 'Answer it under Inventory › Trades.', 'trade');
      }
      if ((me.unreadUpdates ?? 0) > (p.unreadUpdates ?? 0)) popUp('Something happened', 'You have new updates in Made Men.', 'updates');
    }
    f.auction = me.houseAuction?.status === 'open';
    // The race: a reminder 15 minutes before post time, once per race.
    if (me.nextRaceAt) {
      const left = me.nextRaceAt - Date.now();
      f.race = left > 0 && left <= 15 * 60_000;
      if (f.race && raceWarned.current !== me.nextRaceAt) {
        raceWarned.current = me.nextRaceAt;
        if (p && s.alerts) play('bugle', { volume: 0.5 });
        popUp('The 12:30 race starts soon', 'Place your bets at Five Boroughs Park.', 'race');
      }
    }
    render();
  }, [me]);
}
